/**
 * Tier card — paper magazine masthead. Tier name in display serif + tagline + total value.
 */
import { store } from '../store.ts';
import { TIER_DISPLAY, formatINR } from '../types.ts';
import type { Tier } from '../types.ts';

const TIER_TONE: Record<Tier, { fg: string; accent: string }> = {
  starter: { fg: 'oklch(48% 0.08 28)', accent: 'oklch(82% 0.06 80)' },
  curator: { fg: 'oklch(42% 0.12 28)', accent: 'oklch(78% 0.10 78)' },
  collector: { fg: 'oklch(38% 0.14 25)', accent: 'oklch(72% 0.13 75)' },
  connoisseur: { fg: 'oklch(34% 0.16 22)', accent: 'oklch(68% 0.15 72)' },
  tycoon: { fg: 'oklch(30% 0.18 20)', accent: 'oklch(62% 0.18 68)' },
};

export class AlmariTierCard extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });
  #unsub?: () => void;

  connectedCallback(): void {
    this.#unsub = store.subscribe((s) => {
      if (s.summary) this.#render(s.summary.tier, s.summary.total_current_value_inr, s.summary.total_items);
    });
  }

  disconnectedCallback(): void {
    this.#unsub?.();
  }

  #render(tier: Tier, value: number, count: number): void {
    const tone = TIER_TONE[tier];
    const meta = TIER_DISPLAY[tier];
    this.#shadow.innerHTML = /* html */ `
<style>
:host {
  display: block;
  background: var(--paper-card);
  border: 1px solid var(--rule);
  border-radius: 8px;
  padding: 34px 28px 36px;
  position: relative;
  overflow: hidden;
  isolation: isolate;
}
:host::before {
  content: '';
  position: absolute;
  inset: 0;
  background:
    radial-gradient(circle at 88% 12%, ${tone.accent} 0%, transparent 38%),
    linear-gradient(165deg, ${tone.accent} 0%, transparent 50%);
  opacity: 0.42;
  z-index: -1;
}
:host::after {
  content: '';
  position: absolute;
  inset: 6px;
  border: 1px solid color-mix(in oklch, ${tone.fg} 18%, transparent);
  border-radius: 4px;
  pointer-events: none;
  z-index: -1;
}
.eyebrow {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  padding-bottom: 4px;
}
.label {
  font-family: var(--mono);
  font-size: 9px;
  letter-spacing: 0.42em;
  text-transform: uppercase;
  color: ${tone.fg};
  opacity: 0.78;
}
.label-mark {
  font-family: var(--mono);
  font-size: 9px;
  letter-spacing: 0.42em;
  text-transform: uppercase;
  color: var(--ink-faint);
}
.hindi {
  font-family: var(--hindi);
  font-size: clamp(28px, 7.5cqw, 40px);
  font-weight: 400;
  margin-top: 18px;
  color: ${tone.fg};
  letter-spacing: -0.01em;
  line-height: 0.95;
}
.name {
  font-family: var(--display);
  font-size: clamp(40px, 11cqw, 64px);
  font-weight: 700;
  margin-top: 2px;
  letter-spacing: -0.015em;
  color: ${tone.fg};
  font-style: italic;
  line-height: 0.95;
}
.rule {
  width: 56px;
  height: 1px;
  background: ${tone.fg};
  opacity: 0.45;
  margin: 18px 0 16px;
}
.tagline {
  font-family: var(--display);
  font-style: italic;
  font-size: 20px;
  color: var(--ink-dim);
  font-weight: 500;
  line-height: 1.34;
  text-wrap: pretty;
  max-width: 28ch;
}
.tagline::before { content: '\\201C'; color: ${tone.fg}; opacity: 0.6; margin-right: 2px; }
.tagline::after  { content: '\\201D'; color: ${tone.fg}; opacity: 0.6; margin-left: 2px; }
.trajectory {
  font-family: var(--mono);
  font-size: 9px;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  color: var(--ink-faint);
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid color-mix(in oklch, ${tone.fg} 14%, transparent);
}
.foot {
  display: flex;
  align-items: baseline;
  gap: 14px;
  margin-top: 22px;
  padding-top: 18px;
  border-top: 1px dashed var(--rule);
}
.foot-val {
  font-family: var(--display);
  font-size: clamp(34px, 10cqw, 50px);
  font-weight: 700;
  color: var(--ink);
  letter-spacing: -0.02em;
  line-height: 1;
  font-feature-settings: "tnum" 1;
}
.foot-cap {
  font-family: var(--mono);
  font-size: 10px;
  color: var(--ink-faint);
  letter-spacing: 0.3em;
  text-transform: uppercase;
}
</style>
<div class="eyebrow">
  <span class="label">aapki almari ki kahaani</span>
  <span class="label-mark">Tier · ${meta.name}</span>
</div>
<div class="hindi">${meta.hindi}</div>
<div class="name">${meta.name}</div>
<div class="rule"></div>
<div class="tagline">${meta.tagline}</div>
<div class="trajectory">${meta.trajectory}</div>
<div class="foot">
  <span class="foot-val">${formatINR(value)}</span>
  <span class="foot-cap">${count} pieces · ${tier}</span>
</div>
`;
  }
}

customElements.define('almari-tier-card', AlmariTierCard);
